import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import getAPIbaseURL from "../../utils/helpers";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Tab,
  Tabs,
  Button,
  Table,
  Form,
  Spinner,
  Modal,
  Alert,
  Card,
  Row,
  Col,
  Badge,
  ListGroup,
} from "react-bootstrap";
import {
  FaArrowLeft,
  FaSave,
  FaUserPlus,
  FaTrash,
  FaChevronLeft,
  FaChevronRight,
  FaMapMarkerAlt,
  FaUsers,
  FaCog,
  FaCalendarPlus,
} from "react-icons/fa";
import {
  fetchDelegationById,
  updateDelegation,
  removeDelegationMember,
  addDelegationMember,
  fetchDelegationHolidays,
  fetchOfficialDelegationHolidays,
  createDelegationHoliday,
  deleteDelegationHoliday,
  excludeOfficialHoliday,
  fetchSubdivisions,
  importDelegationHolidays,
} from "../../redux/slices/delegationSlice";
import { fetchRoles } from "../../redux/slices/rolesSlice";
import { getFullName } from "../../utils/helpers";

const API_URL = getAPIbaseURL();
const MEMBERS_PER_PAGE = 10;
const AVATAR_PALETTE = [
  "#F87171", "#FB923C", "#FBBF24", "#34D399", "#22D3EE", "#60A5FA",
  "#818CF8", "#C084FC", "#F472B6", "#4ADE80", "#38BDF8", "#FCA5A5",
];

const isPastYear = (dateString) => {
  if (!dateString) return false;
  const holidayYear = new Date(dateString).getFullYear();
  const currentYear = new Date().getFullYear();
  return holidayYear < currentYear;
};

const getAvatarColor = (user) => {
  const name = `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim();
  const fallback = user?.username ?? "U";
  const finalName = name || fallback;
  const code = finalName.toUpperCase().charCodeAt(0) || 0;
  return AVATAR_PALETTE[code % AVATAR_PALETTE.length];
};

const DelegationDetailsPage = () => {
  const { t, i18n } = useTranslation();
  const holidayLanguage = t.language?.startsWith("pt") ? "PT" : "EN";
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString(i18n.language?.startsWith("pt") ? "pt-PT" : "en-US");
  };

  const currentUser = useSelector((state) => state.auth.user);
  const role = currentUser?.role?.name || "";
  const normalized = role.replace(/\s+/g, "").toUpperCase();
  const isAdmin = normalized === "SUPERADMIN" || normalized === "MANAGER";

  const { subdivisions, isLoading: loadingSubdivisions, officialHolidays, current: delegation, isLoading } =
    useSelector((state) => state.delegations);

  const [holidayYear, setHolidayYear] = useState(new Date().getFullYear());
  const [importYear, setImportYear] = useState(new Date().getFullYear());
  const [activeTab, setActiveTab] = useState("overview");
  const [page, setPage] = useState(1);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [formData, setFormData] = useState({ name: "", location: "", description: "", subdivision_code: "" });
  const [showAddModal, setShowAddModal] = useState(false);
  const [newMemberId, setNewMemberId] = useState("");
  const [newHoliday, setNewHoliday] = useState({ name: "", date: "", target: "delegation", user_id: "" });
  const [importSubdivision, setImportSubdivision] = useState("");
  const [importing, setImporting] = useState(false);
  const [addingHoliday, setAddingHoliday] = useState(false);

  const roles = useSelector((state) => state.roles.items);
  const allUsers = useSelector((state) => state.users?.items) || [];

  useEffect(() => {
    if (id) {
      dispatch(fetchDelegationById(id));
      dispatch(fetchDelegationHolidays(id));
      dispatch(fetchOfficialDelegationHolidays({ delegationId: id, year: holidayYear, languageIsoCode: holidayLanguage }));
      if (Object.keys(subdivisions || {}).length === 0) {
        dispatch(fetchSubdivisions());
      }
    }
    dispatch(fetchRoles());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, id, holidayYear]);

  useEffect(() => {
    if (delegation) {
      setFormData({
        name: delegation.name || "",
        location: delegation.location || "",
        description: delegation.description || "",
        subdivision_code: delegation.subdivision_code || "",
      });
    }
  }, [delegation]);

  const members = useMemo(
    () => delegation?.users || delegation?.memberships || delegation?.members || [],
    [delegation]
  );
  const memberUsers = members.map((m) => m.user || m);

  const allHolidays = useMemo(() => {
    const customRows = delegation?.holidays || [];
    const excludedDates = new Set(customRows.filter((h) => h.excluded).map((h) => h.date));

    const custom = customRows
      .filter((h) => !h.excluded)
      .filter((h) => new Date(h.date).getFullYear() === holidayYear)
      .map((h) => ({ id: h.id, name: h.name, date: h.date, isCustom: true, userId: h.user_id || null }));

    const official = (officialHolidays || [])
      .filter((h) => !excludedDates.has(h.date))
      .map((h, index) => ({ id: `off-${index}-${h.date}`, name: h.name, date: h.date, isCustom: false, userId: null }));

    return [...custom, ...official].sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [delegation?.holidays, officialHolidays, holidayYear]);

  const getSubdivisionLabel = (code) => {
    if (!code) return "N/A";
    const entry = Object.entries(subdivisions || {}).find(([, c]) => c === code);
    return entry ? entry[0] : code;
  };

  const getMemberName = (userId) => {
    if (!userId) return null;
    const u = memberUsers.find((m) => m.id === userId);
    return u ? (getFullName(u) || u.username) : "N/A";
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setUpdating(true);
    try {
      await dispatch(updateDelegation({ delegationId: id, updates: formData })).unwrap();
      await dispatch(fetchDelegationById(id));
      setSuccess(t("pages.DelegationDetailsPage.updateSuccess", { defaultValue: "Delegação atualizada com sucesso." }));
    } catch (err) {
      setError(err.message || t("pages.DelegationDetailsPage.updateError", { defaultValue: "Falha ao atualizar delegação" }));
    } finally {
      setUpdating(false);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm(t("pages.DelegationDetailsPage.confirmRemoveMember", { defaultValue: "Tem a certeza que quer remover este membro?" }))) return;
    setError(null);
    try {
      await dispatch(removeDelegationMember({ delegationId: id, userId })).unwrap();
      await dispatch(fetchDelegationById(id));
    } catch (err) {
      setError(err.message || t("pages.DelegationDetailsPage.removeMemberError", { defaultValue: "Falha ao remover membro" }));
    }
  };

  const handleExcludeOfficialHoliday = async (holiday) => {
    if (!window.confirm(t("pages.DelegationDetailsPage.confirmExcludeHoliday", { defaultValue: 'Excluir "{{name}}" desta delegação?', name: holiday.name }))) return;
    setError(null);
    try {
      await dispatch(excludeOfficialHoliday({ delegationId: id, name: holiday.name, date: holiday.date })).unwrap();
      await dispatch(fetchDelegationHolidays(id)).unwrap();
      setSuccess(t("pages.DelegationDetailsPage.excludeHolidaySuccess", { defaultValue: "Feriado oficial excluído!" }));
    } catch (err) {
      setError(err.message || t("pages.DelegationDetailsPage.excludeHolidayError", { defaultValue: "Falha ao excluir feriado" }));
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    setError(null);
    if (!newMemberId) {
      setError(t("pages.DelegationDetailsPage.selectUserError", { defaultValue: "Selecione um utilizador." }));
      return;
    }
    try {
      await dispatch(addDelegationMember({ delegationId: id, userId: newMemberId })).unwrap();
      await dispatch(fetchDelegationById(id));
      setShowAddModal(false);
      setNewMemberId("");
    } catch (err) {
      setError(err.message || t("pages.DelegationDetailsPage.addMemberError", { defaultValue: "Falha ao adicionar membro" }));
    }
  };

  const handleImportHolidays = async () => {
    setError(null);
    setSuccess(null);
    setImporting(true);

    try {
      await dispatch(
        importDelegationHolidays({
          delegationId: id,
          year: importYear,
          subdivisionCode: importSubdivision || undefined,
          languageIsoCode: holidayLanguage,
        })
      ).unwrap();

      await dispatch(fetchDelegationHolidays(id));
      await dispatch(fetchOfficialDelegationHolidays({ delegationId: id, year: holidayYear, languageIsoCode: holidayLanguage }));

      setSuccess(t("pages.DelegationDetailsPage.importSuccess", { defaultValue: "Feriados importados com sucesso." }));
    } catch (err) {
      setError(err.message || t("pages.DelegationDetailsPage.importError", { defaultValue: "Erro ao importar feriados." }));
    } finally {
      setImporting(false);
    }
  };

  const handleAddHoliday = async (e) => {
    e.preventDefault();
    setError(null);

    if (!newHoliday.name || !newHoliday.date) {
      setError(t("pages.DelegationDetailsPage.fillNameDateError", { defaultValue: "Preencha o nome e a data." }));
      return;
    }
    if (newHoliday.target === "member" && !newHoliday.user_id) {
      setError(t("pages.DelegationDetailsPage.selectMemberError", { defaultValue: "Selecione o colaborador." }));
      return;
    }

    setAddingHoliday(true);
    try {
      await dispatch(
        createDelegationHoliday({
          delegationId: id,
          holidayData: {
            name: newHoliday.name,
            date: newHoliday.date,
            user_id: newHoliday.target === "member" ? newHoliday.user_id : null,
          },
        })
      ).unwrap();
      await dispatch(fetchDelegationHolidays(id)).unwrap();
      await dispatch(fetchOfficialDelegationHolidays({ delegationId: id, year: holidayYear, languageIsoCode: holidayLanguage }));

      setNewHoliday({ name: "", date: "", target: "delegation", user_id: "" });
      setSuccess(t("pages.DelegationDetailsPage.addHolidaySuccess", { defaultValue: "Feriado adicionado!" }));
    } catch (err) {
      setError(err.message || t("pages.DelegationDetailsPage.addHolidayError", { defaultValue: "Falha ao adicionar feriado" }));
    } finally {
      setAddingHoliday(false);
    }
  };

  const handleDeleteHoliday = async (holidayId) => {
    if (!window.confirm(t("pages.DelegationDetailsPage.confirmDeleteHoliday", { defaultValue: "Tem a certeza que quer apagar este feriado?" }))) return;
    setError(null);
    try {
      await dispatch(deleteDelegationHoliday(holidayId)).unwrap();
      await dispatch(fetchDelegationHolidays(id)).unwrap();
      setSuccess(t("pages.DelegationDetailsPage.deleteHolidaySuccess", { defaultValue: "Feriado removido!" }));
    } catch (err) {
      setError(err.message || t("pages.DelegationDetailsPage.deleteHolidayError", { defaultValue: "Falha ao remover feriado" }));
    }
  };

  const totalPages = Math.max(1, Math.ceil(members.length / MEMBERS_PER_PAGE));
  const paginatedMembers = useMemo(
    () => members.slice((page - 1) * MEMBERS_PER_PAGE, page * MEMBERS_PER_PAGE),
    [members, page]
  );

  const availableUsersToAdd = allUsers.filter(
    (u) => !members.some((m) => (m.user?.id || m.id) === u.id)
  );

  if (isLoading || !delegation) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <Spinner animation="border" variant="danger" />
      </div>
    );
  }

  return (
    <div className="delegation-details-page pb-5">
      <div className="d-flex align-items-center gap-3 mb-4 border-bottom pb-3" style={{ marginTop: "10px" }}>
        <Button variant="light" className="bg-surface shadow-sm rounded-circle p-2" onClick={() => navigate(-1)}>
          <FaArrowLeft className="txt-subtle" />
        </Button>
        <div className="flex-grow-1">
          <h3 className="m-0 fw-bold txt-main">{delegation.name}</h3>
          <small className="txt-subtle">
            <FaMapMarkerAlt className="me-1" />
            {delegation.location || t("pages.DelegationDetailsPage.noLocation", { defaultValue: "Sem localização" })}
            {delegation.subdivision_code && (
              <Badge bg="secondary" className="ms-2">{getSubdivisionLabel(delegation.subdivision_code)}</Badge>
            )}
          </small>
        </div>
        <Badge bg="light" text="dark" className="p-2 shadow-sm">
          <FaUsers className="me-1" /> {t("pages.DelegationDetailsPage.memberCount", { defaultValue: "{{count}} membros", count: members.length })}
        </Badge>
      </div>

      {error && <Alert variant="danger" className="mb-3" onClose={() => setError(null)} dismissible>{error}</Alert>}
      {success && <Alert variant="success" className="mb-3" onClose={() => setSuccess(null)} dismissible>{success}</Alert>}

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4 custom-tabs">
        <Tab eventKey="overview" title={t("pages.DelegationDetailsPage.overviewTab", { defaultValue: "Visão Geral" })}>
          <Row className="g-3">
            <Col md={8}>
              <Card className="shadow-sm border-0 h-100">
                <Card.Body>
                  <Row className="g-4">
                    <Col md={6}>
                      <small className="txt-subtle fw-bold text-uppercase d-block mb-1">
                        {t("pages.DelegationDetailsPage.description", { defaultValue: "Descrição" })}
                      </small>
                      <span className="txt-main">
                        {delegation.description || t("pages.DelegationDetailsPage.noDescription", { defaultValue: "Sem descrição" })}
                      </span>
                    </Col>
                    <Col md={6}>
                      <small className="txt-subtle fw-bold text-uppercase d-block mb-1">
                        {t("pages.DelegationDetailsPage.subdivisionApi", { defaultValue: "Subdivisão" })}
                      </small>
                      <span className="txt-main">{getSubdivisionLabel(delegation.subdivision_code)}</span>
                    </Col>
                    <Col md={6}>
                      <small className="txt-subtle fw-bold text-uppercase d-block mb-1">
                        {t("pages.DelegationDetailsPage.createdAt", { defaultValue: "Criada em" })}
                      </small>
                      <span className="txt-main">{formatDate(delegation.created_at)}</span>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="shadow-sm border-0 h-100 text-center d-flex justify-content-center">
                <Card.Body>
                  <FaUsers size={28} className="txt-subtle mb-2" />
                  <h2 className="fw-bold txt-main m-0">{members.length}</h2>
                  <small className="txt-subtle">{t("pages.DelegationDetailsPage.collaborators", { defaultValue: "colaboradores" })}</small>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {delegation.location && (
            <Card className="shadow-sm border-0 mt-3">
              <Card.Body>
                <small className="txt-subtle fw-bold text-uppercase d-block mb-2">
                  {t("pages.DelegationDetailsPage.mapLocation", { defaultValue: "Localização no Mapa" })}
                </small>
                <iframe
                  title={t("pages.DelegationDetailsPage.mapTitle", { defaultValue: "Mapa da Delegação" })}
                  className="w-100 rounded"
                  height="300"
                  loading="lazy"
                  src={`https://www.google.com/maps?q=${encodeURIComponent(delegation.location)}&output=embed`}
                  style={{ border: "var(--border-thin)" }}
                ></iframe>
              </Card.Body>
            </Card>
          )}
        </Tab>

        <Tab eventKey="members" title={t("pages.DelegationDetailsPage.membersTab", { defaultValue: "Membros ({{count}})", count: members.length })}>
          <Card className="shadow-sm border-0 overflow-hidden">
            {isAdmin && (
              <div className="p-3 border-bottom d-flex justify-content-between align-items-center">
                <Button variant="primary" size="sm" onClick={() => setShowAddModal(true)}>
                  <FaUserPlus className="me-1" /> {t("pages.DelegationDetailsPage.addMember", { defaultValue: "Adicionar Membro" })}
                </Button>
              </div>
            )}
            <Table hover responsive className="mb-0 align-middle">
              <thead className="bg-surface">
                <tr>
                  <th className="txt-subtle fw-semibold px-4 py-3 border-0">{t("pages.DelegationDetailsPage.collaborator", { defaultValue: "Colaborador" })}</th>
                  <th className="txt-subtle fw-semibold px-4 py-3 border-0">{t("pages.DelegationDetailsPage.roleLabel", { defaultValue: "Função" })}</th>
                  {isAdmin && <th className="txt-subtle fw-semibold px-4 py-3 border-0 text-end">{t("pages.DelegationDetailsPage.actions", { defaultValue: "Ações" })}</th>}
                </tr>
              </thead>
              <tbody>
                {paginatedMembers.map((item, index) => {
                  const userData = item.user || item;
                  const displayName = getFullName(userData) || userData.username || "N/A";
                  const matchedRole = roles.find((r) => r.id === userData.role_id);
                  const profilePic = userData.profile_picture || userData.pic;
                  const imgSrc = profilePic ? (profilePic.startsWith("http") ? profilePic : `${API_URL}${profilePic}`) : null;

                  return (
                    <tr key={userData.id || index} style={{ borderBottom: "var(--border-thin)" }}>
                      <td className="px-4 py-3">
                        <div className="d-flex align-items-center gap-3">
                          {imgSrc ? (
                            <img src={imgSrc} alt={displayName} className="rounded-circle" style={{ width: 40, height: 40, objectFit: "cover" }} />
                          ) : (
                            <div className="d-flex align-items-center justify-content-center fw-bold" style={{ width: 40, height: 40, borderRadius: "50%", backgroundColor: getAvatarColor(userData), color: "var(--white)" }}>
                              {displayName.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <span className="fw-semibold txt-main">{displayName}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 fw-semibold txt-main">{matchedRole?.name || "N/A"}</td>
                      {isAdmin && (
                        <td className="px-4 py-3 text-end">
                          <Button variant="outline-danger" size="sm" onClick={() => handleRemoveMember(userData.id)} disabled={isPastYear(item.date)}>
                            <FaTrash />
                          </Button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </Table>

            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center px-4 py-3 bg-surface">
                <span className="txt-subtle text-small">
                  {t("pages.DelegationDetailsPage.pageOf", { defaultValue: "Página {{page}} de {{total}}", page, total: totalPages })}
                </span>
                <div className="d-flex align-items-center gap-2">
                  <Button variant="outline-secondary" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                    <FaChevronLeft size={12} />
                  </Button>
                  <Button variant="outline-secondary" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                    <FaChevronRight size={12} />
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </Tab>

        {isAdmin && (
          <Tab eventKey="settings" title={<><FaCog className="me-1" />{t("pages.DelegationDetailsPage.settingsTab", { defaultValue: "Configurações" })}</>}>
            <Row className="g-3">
              <Col md={6}>
                <Card className="shadow-sm border-0 h-100">
                  <Card.Body>
                    <h6 className="fw-bold txt-main mb-3">{t("pages.DelegationDetailsPage.delegationData", { defaultValue: "Dados da Delegação" })}</h6>
                    <Form onSubmit={handleUpdate}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold txt-main">{t("pages.DelegationDetailsPage.name", { defaultValue: "Nome" })}</Form.Label>
                        <Form.Control value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold txt-main">{t("pages.DelegationDetailsPage.location", { defaultValue: "Localização" })}</Form.Label>
                        <Form.Control value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold txt-main">{t("pages.DelegationDetailsPage.description", { defaultValue: "Descrição" })}</Form.Label>
                        <Form.Control as="textarea" rows={3} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
                      </Form.Group>
                      <Form.Group className="mb-4">
                        <Form.Label className="fw-bold txt-main">{t("pages.DelegationDetailsPage.subdivision", { defaultValue: "Subdivisão" })}</Form.Label>
                        <Form.Select
                          value={formData.subdivision_code}
                          onChange={(e) => setFormData({ ...formData, subdivision_code: e.target.value })}
                          disabled={loadingSubdivisions}
                        >
                          <option value="">{t("pages.DelegationDetailsPage.nationalOnly", { defaultValue: "Nacional (apenas feriados comuns)" })}</option>
                          {Object.entries(subdivisions || {})
                            .sort(([a], [b]) => a.localeCompare(b))
                            .map(([name, code]) => (
                              <option key={code} value={code}>{name}</option>
                            ))}
                        </Form.Select>
                      </Form.Group>
                      <div className="d-flex justify-content-end">
                        <Button type="submit" variant="danger" disabled={updating}>
                          <FaSave className="me-1" />{" "}
                          {updating
                            ? t("pages.DelegationDetailsPage.saving", { defaultValue: "A guardar..." })
                            : t("pages.DelegationDetailsPage.saveSettings", { defaultValue: "Salvar Definições" })}
                        </Button>
                      </div>
                    </Form>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6}>
                <Card className="shadow-sm border-0 h-100">
                  <Card.Body>
                    <h6 className="fw-bold txt-main mb-1">{t("pages.DelegationDetailsPage.holidaysTitle", { defaultValue: "Feriados" })}</h6>

                    <div className="d-flex align-items-center justify-content-center mb-3">
                      <Button variant="light" size="sm" onClick={() => setHolidayYear((y) => y - 1)}>
                        <FaChevronLeft />
                      </Button>
                      <span className="mx-3 fw-bold">{holidayYear}</span>
                      <Button variant="light" size="sm" onClick={() => setHolidayYear((y) => y + 1)}>
                        <FaChevronRight />
                      </Button>
                    </div>

                    <Form onSubmit={handleAddHoliday} className="mb-4 p-3 bg-surface rounded">
                      <Row className="g-2 mb-2">
                        <Col xs={7}>
                          <Form.Control
                            size="sm"
                            placeholder={t("pages.DelegationDetailsPage.holidayNamePlaceholder", { defaultValue: "Nome (ex: Carnaval)" })}
                            value={newHoliday.name}
                            onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })}
                            required
                          />
                        </Col>
                        <Col xs={5}>
                          <Form.Control
                            size="sm"
                            type="date"
                            value={newHoliday.date}
                            onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })}
                            required
                          />
                        </Col>
                      </Row>

                      <div className="d-flex gap-3 mb-2">
                        <Form.Check
                          type="radio"
                          id="holiday-target-delegation"
                          name="holidayTarget"
                          label={t("pages.DelegationDetailsPage.wholeDelegation", { defaultValue: "Toda a delegação ({{count}})", count: members.length })}
                          checked={newHoliday.target === "delegation"}
                          onChange={() => setNewHoliday({ ...newHoliday, target: "delegation", user_id: "" })}
                        />
                      </div>

                      <Button size="sm" type="submit" variant="primary" disabled={addingHoliday}>
                        <FaCalendarPlus className="me-1" />{" "}
                        {addingHoliday
                          ? t("pages.DelegationDetailsPage.addingHoliday", { defaultValue: "A adicionar..." })
                          : t("pages.DelegationDetailsPage.addHoliday", { defaultValue: "Adicionar Feriado" })}
                      </Button>
                    </Form>

                    <h6 className="fw-bold txt-main mb-3">{t("pages.DelegationDetailsPage.importHolidays", { defaultValue: "Importar Feriados" })}</h6>

                    <Form className="bg-surface rounded p-3 mb-4">
                      <Row className="mb-3">
                        <Col md={4}>
                          <Form.Label>{t("pages.DelegationDetailsPage.year", { defaultValue: "Ano" })}</Form.Label>
                          <Form.Control type="number" value={importYear} onChange={(e) => setImportYear(Number(e.target.value))} />
                        </Col>
                        <Col md={8}>
                          <Form.Label>{t("pages.DelegationDetailsPage.source", { defaultValue: "Origem" })}</Form.Label>
                          <Form.Select value={importSubdivision} onChange={(e) => setImportSubdivision(e.target.value)}>
                            <option value="">{t("pages.DelegationDetailsPage.nationalImport", { defaultValue: "Portugal (Apenas feriados nacionais)" })}</option>
                            {Object.entries(subdivisions || {})
                              .sort(([a], [b]) => a.localeCompare(b))
                              .map(([name, code]) => (
                                <option key={code} value={code}>{name}</option>
                              ))}
                          </Form.Select>
                        </Col>
                      </Row>

                      <Button variant="success" onClick={handleImportHolidays} disabled={importing}>
                        {importing
                          ? t("pages.DelegationDetailsPage.importing", { defaultValue: "Importando..." })
                          : t("pages.DelegationDetailsPage.import", { defaultValue: "Importar" })}
                      </Button>
                    </Form>

                    <h6 className="fw-bold txt-main mb-2" style={{ fontSize: "14px" }}>
                      {t("pages.DelegationDetailsPage.allHolidays", { defaultValue: "Todos os Feriados ({{count}})", count: allHolidays.length })}
                    </h6>

                    {allHolidays.length > 0 ? (
                      <ListGroup style={{ maxHeight: "280px", overflowY: "auto" }}>
                        {allHolidays.map((h) => (
                          <ListGroup.Item key={h.id} className="d-flex justify-content-between align-items-center py-2">
                            <div>
                              <div className="fw-semibold txt-main" style={{ fontSize: "14px" }}>{h.name}</div>
                              <small className="txt-subtle">
                                {formatDate(h.date)}
                                {h.userId && <> · {getMemberName(h.userId)}</>}
                              </small>
                              <div> 
                                <Badge bg={h.isCustom ? "warning" : "info"}>
                                  {h.isCustom
                                    ? (h.userId
                                      ? t("pages.DelegationDetailsPage.personal", { defaultValue: "Pessoal" })
                                      : t("pages.DelegationDetailsPage.delegationBadge", { defaultValue: "Delegação" }))
                                    : t("pages.DelegationDetailsPage.official", { defaultValue: "Oficial" })}
                                </Badge>
                              </div>
                            </div>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              disabled={isPastYear(h.date)}
                              onClick={() => {
                                if (isPastYear(h.date)) return;

                                h.isCustom
                                  ? handleDeleteHoliday(h.id)
                                  : handleExcludeOfficialHoliday(h);
                              }}
                            >
                              <FaTrash />
                            </Button>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    ) : (
                      <div className="text-center p-3 bg-surface rounded txt-subtle">
                        {t("pages.DelegationDetailsPage.noHolidays", { defaultValue: "Nenhum feriado." })}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab>
        )}
      </Tabs>

      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t("pages.DelegationDetailsPage.addMemberModalTitle", { defaultValue: "Adicionar Colaborador" })}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAddMember}>
          <Modal.Body>
            <Form.Group>
              <Form.Label>{t("pages.DelegationDetailsPage.selectUserLabel", { defaultValue: "Selecione o Utilizador" })}</Form.Label>
              <Form.Select value={newMemberId} onChange={(e) => setNewMemberId(e.target.value)} required>
                <option value="">{t("pages.DelegationDetailsPage.searchUserPlaceholder", { defaultValue: "Procurar na base de dados..." })}</option>
                {availableUsersToAdd.map((u) => (
                  <option key={u.id} value={u.id}>{getFullName(u) || u.username}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>{t("pages.DelegationDetailsPage.cancel", { defaultValue: "Cancelar" })}</Button>
            <Button variant="primary" type="submit">{t("pages.DelegationDetailsPage.addToDelegation", { defaultValue: "Adicionar à Delegação" })}</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default DelegationDetailsPage;